import type { FC } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import RichTextComponent from '@eneco/components/RichText/RichText';
import { ButtonLink, Heading, Stack, StepList, TextLink } from '@eneco/sparky';

import type { StepExplainerContent } from './step-explainer-content.types';

/**
 * `StepExplainer` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/StepExplainerVertical/
 * StepExplainerVertical.tsx`, registered under the `StepExplainer` componentName — see
 * `containers/*\/sitecoreComponents.ts`): an optional heading above a `StepList`, one item per step,
 * each with its rich-text description, an optional button and/or text link, and an image.
 *
 * Known simplification: see `step-explainer-content.types.ts` — the generated Sanity schema has no
 * `items` field at all yet, so the steps themselves cannot render on `?_cms=sanity` until
 * `migrator schema-gen` is re-run; only the title shows today.
 */
const StepExplainer: FC = () => {
  const { adapter } = useCms();
  const content = useContent<StepExplainerContent>();
  const mapImage = useImageMapper();
  const variant = (useSetting('variant') || 'interactive') as 'default' | 'interactive';

  const steps = content.items ?? [];

  return (
    <Stack gap="10">
      {content.title && (
        <Heading as="h2" size="M">
          {content.title}
        </Heading>
      )}
      <StepList variant={variant}>
        {steps.map((step, index) => {
          const imageProps = mapImage(step.image);
          const html = adapter.toRichText(step.content);
          const buttonLink = adapter.toLink(step.buttonLink);
          const link = adapter.toLink(step.link);

          return (
            <StepList.Item
              key={step._key ?? String(index)}
              description={
                <Stack gap="6">
                  <Stack.Item>{html ? <RichTextComponent html={html} /> : null}</Stack.Item>
                  {buttonLink?.href ? <ButtonLink href={buttonLink.href}>{buttonLink.text}</ButtonLink> : null}
                  {link?.href ? <TextLink href={link.href}>{link.text}</TextLink> : null}
                </Stack>
              }
              image={{
                alt: imageProps?.alt ?? '',
                src: imageProps?.src ?? '',
                sources: imageProps?.sources,
              }}>
              {step.title}
            </StepList.Item>
          );
        })}
      </StepList>
    </Stack>
  );
};

export default StepExplainer;
